import React from "react";
import { useTranslation } from "contexts/Safra/TranslationContext";
import TextInput from "components/TextInput";
import DateInput from "components/DateInput";
import SelectInput from "components/SelectInput";
import DocumentTypeSelector from "components/DocumentTypeSelector";
import DocumentInput from "components/DocumentInput";
import { COUNTRIES, NATIONALITIES } from "utils/countries";
import {
  FormBlock,
  PassengerTitle,
  CheckboxWrapper,
  Checkbox,
  FieldRow,
} from "./styles";

export default function PassengerForm({
  index,
  showSelfCheckbox,
  formik,
  customerData,
}) {
  const { t } = useTranslation();
  
  const GENDER_OPTIONS = [
    { value: "M", label: t('safra.passage_resume.fields.genders.male') },
    { value: "F", label: t('safra.passage_resume.fields.genders.female') },
  ];

  const prefix = `passengers[${index}]`;
  const passenger = formik.values.passengers[index];
  const errors = formik.errors.passengers?.[index] || {};
  const touched = formik.touched.passengers?.[index] || {};
  const submitted = formik.submitCount > 0;

  const getFieldProps = (field) => ({
    name: `${prefix}.${field}`,
    value: passenger[field],
    onChange: formik.handleChange,
    onBlur: formik.handleBlur,
    error:
      (touched[field] || submitted) && errors[field]
        ? errors[field]
        : undefined,
  });

  const handleSelfCheckbox = (e) => {
    const checked = e.target.checked;
    formik.setFieldValue(`${prefix}.isSelf`, checked);

    if (checked && customerData) {
      formik.setFieldValue(`${prefix}.firstName`, customerData.firstName || "");
      formik.setFieldValue(`${prefix}.lastName`, customerData.lastName || "");
      if (customerData.document) {
        formik.setFieldValue(`${prefix}.document`, customerData.document);
        formik.setFieldValue(`${prefix}.documentType`, "CPF");
      }

      const fields = ['firstName', 'lastName', 'birthDate', 'gender', 'documentType', 'document'];
      setTimeout(() => {
        fields.forEach((field) => formik.setFieldTouched(`${prefix}.${field}`, true, true));
      }, 0);
    } else if (!checked) {
      formik.setFieldValue(`${prefix}.firstName`, "");
      formik.setFieldValue(`${prefix}.lastName`, "");
      formik.setFieldValue(`${prefix}.document`, "");
    }
  };

  const handleDocumentTypeChange = (e) => {
    formik.handleChange(e);
    formik.setFieldValue(`${prefix}.document`, "");
    formik.setFieldValue(`${prefix}.passportNumber`, "");
    formik.setFieldValue(`${prefix}.passportValidity`, "");
    formik.setFieldValue(`${prefix}.passportNationality`, "");
    formik.setFieldValue(`${prefix}.passportIssuingCountry`, "");
    formik.setFieldTouched(`${prefix}.document`, false);
    formik.setFieldTouched(`${prefix}.passportNumber`, false);
    formik.setFieldTouched(`${prefix}.passportValidity`, false);
    formik.setFieldTouched(`${prefix}.passportNationality`, false);
    formik.setFieldTouched(`${prefix}.passportIssuingCountry`, false);
  };

  const isSelf = passenger.isSelf || false;

  return (
    <FormBlock>
      <PassengerTitle>{t('safra.passenger_data.form.passenger_label', { index: index + 1 })}</PassengerTitle>

      {showSelfCheckbox && (
        <CheckboxWrapper>
          <Checkbox
            type="checkbox"
            checked={isSelf}
            onChange={handleSelfCheckbox}
          />
          {t('safra.passenger_data.form.is_self')}
        </CheckboxWrapper>
      )}

      <TextInput placeholder={t('safra.passenger_data.form.first_name')} {...getFieldProps("firstName")} />

      <TextInput
        placeholder={t('safra.passenger_data.form.last_name')}
        {...getFieldProps("lastName")}
      />

      <FieldRow>
        <DateInput
          placeholder={t('safra.passenger_data.form.birth_date')}
          {...getFieldProps("birthDate")}
        />
        <SelectInput
          options={GENDER_OPTIONS}
          placeholder={t('safra.passenger_data.form.gender')}
          {...getFieldProps("gender")}
        />
      </FieldRow>

      <DocumentTypeSelector
        name={`${prefix}.documentType`}
        value={passenger.documentType}
        onChange={handleDocumentTypeChange}
        error={
          (touched.documentType || submitted) && errors.documentType
            ? errors.documentType
            : undefined
        }
      />

      {passenger.documentType === "CPF" ? (
        <DocumentInput
          documentType="CPF"
          {...getFieldProps("document")}
          disabled={isSelf}
        />
      ) : (
        <>
          <FieldRow>
            <TextInput
              placeholder={t('safra.passenger_data.form.passport_number')}
              {...getFieldProps("passportNumber")}
            />
            <DateInput
              placeholder={t('safra.passenger_data.form.validity')}
              standalone
              {...getFieldProps("passportValidity")}
            />
          </FieldRow>
          <FieldRow>
            <SelectInput
              options={COUNTRIES}
              placeholder={t('safra.passenger_data.form.issuing_country')}
              {...getFieldProps("passportIssuingCountry")}
            />
            <SelectInput
              options={NATIONALITIES}
              placeholder={t('safra.passenger_data.form.nationality')}
              {...getFieldProps("passportNationality")}
            />
          </FieldRow>
        </>
      )}
    </FormBlock>
  );
}
